import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import debounce from 'lodash/debounce';
import store from '@/store';
import { config } from '@/config.js';

/**
 * useSessionsList — listado de sesiones de un evento paginado y buscable en
 * SERVIDOR.
 *
 * Endpoint: `back.events.sesiones.index` → GET /api/v2/events/{evento_id}/sessions
 * (superficie v2, no v1). Espera los filtros { page, per_page, search } y
 * responde con { data: { data, meta } } (meta con current_page, last_page,
 * total, per_page).
 *
 * Antes filtraba en cliente sobre las sesiones embebidas en el evento. Ahora
 * todo va por API: al expandir la ficha se piden solo las sesiones de la
 * página solicitada. Este composable NO cambia si el backend ajusta detalles:
 * la interfaz expuesta (search / page / model / meta / sesiones) es la misma
 * que ya alimenta la ficha y el TableFooter.
 *
 * @param {number|import('vue').Ref<number>} eventoId Id del evento cuyas sesiones se listan.
 * @param {{
 *   perPage?: number,
 *   fetcher?: Function
 * }} [opciones]
 *   fetcher(opciones) — inyectable para tests. Por defecto despacha la acción
 *   Vuex del endpoint real. Debe resolver `{ data: { data, meta } }`.
 * @returns {{
 *   search: import('vue').Ref<string>,
 *   page: import('vue').Ref<number>,
 *   perPage: import('vue').Ref<number>,
 *   sesiones: import('vue').Ref<Array>,
 *   loading: import('vue').Ref<boolean>,
 *   error: import('vue').Ref<unknown>,
 *   model: import('vue').ComputedRef<{page:number, per_page:number}>,
 *   meta: import('vue').ComputedRef<Object>,
 *   totalPaginas: import('vue').ComputedRef<number>,
 *   totalFiltradas: import('vue').ComputedRef<number>,
 *   mostrarBusqueda: import('vue').ComputedRef<boolean>,
 *   mostrarPaginacion: import('vue').ComputedRef<boolean>,
 *   fetch: Function,
 *   reset: Function
 * }}
 */
export function useSessionsList(eventoId, { perPage = 5, fetcher } = {}) {
    const search = ref('');
    const page = ref(1);
    const perPageRef = ref(perPage);
    const sesiones = ref([]);
    const serverMeta = ref({});
    const loading = ref(false);
    const error = ref(null);
    // true sólo cuando la ÚLTIMA petición terminó bien. Es lo que permite
    // distinguir "cargó y no hay sesiones" (total 0 legítimo) de "no cargó"
    // (error o carga todavía no resuelta), que son estados distintos para el
    // usuario: el primero es una respuesta válida, el segundo no.
    const cargado = ref(false);

    // Secuencia de peticiones: cada fetch toma un id incremental y sólo la
    // última puede escribir el estado. Sin esto, una respuesta lenta anterior
    // (término o página previos, o el evento anterior) llegaba DESPUÉS y
    // sustituía filas y metadatos de los filtros vigentes, dejando el listado
    // y su paginación mostrando algo que no corresponde a lo pedido.
    let ultimaPeticion = 0;

    const idActual = computed(() =>
        typeof eventoId === 'function' ? eventoId() : (eventoId && eventoId.value !== undefined ? eventoId.value : eventoId)
    );

    /**
     * Petición por defecto: despacha la acción Vuex del endpoint paginado.
     * El backend responde { data: { data: [...], meta: {...} } }.
     */
    async function dispatchSesiones({ evento_id, filters }) {
        return store.dispatch(config.getVuexAlias('back.events.sesiones.index'), {
            evento_id,
            filters
        });
    }

    const doFetch = fetcher || dispatchSesiones;

    async function fetch() {
        const evento_id = idActual.value;
        if (!evento_id) {
            sesiones.value = [];
            serverMeta.value = {};
            cargado.value = false;
            return;
        }

        // Marca esta petición como la vigente. Toda escritura posterior al
        // await comprueba que sigue siendo la última; si no, se descarta.
        const peticion = ++ultimaPeticion;

        loading.value = true;
        error.value = null;
        cargado.value = false;
        try {
            const res = await doFetch({
                evento_id,
                filters: {
                    page: page.value,
                    per_page: perPageRef.value,
                    search: search.value
                }
            });

            // Respuesta obsoleta (llegó después de una petición más nueva):
            // no toca ni las filas, ni el meta, ni el error, ni `loading`.
            if (peticion !== ultimaPeticion) return;

            const payload = (res && res.data) || {};
            sesiones.value = Array.isArray(payload.data) ? payload.data : [];
            serverMeta.value = payload.meta || {};
            cargado.value = true;
        } catch (e) {
            if (peticion !== ultimaPeticion) return;

            // Un fallo NO es un listado vacío: se expone el error (la ficha lo
            // pinta con opción de reintentar) en vez de dejar el mensaje de
            // "no hay sesiones", que es una respuesta válida con total 0.
            error.value = e;
            sesiones.value = [];
            serverMeta.value = {};
        } finally {
            // Sólo la petición vigente apaga el loader: si una obsoleta lo
            // apagara, la UI dejaría de mostrar la carga en curso.
            if (peticion === ultimaPeticion) loading.value = false;
        }
    }

    const debouncedFetch = debounce(() => fetch(), 400);

    // Silencia el watcher de `search` mientras se cambia de evento, para que
    // el reset de la búsqueda no programe una segunda petición.
    let cambiandoEvento = false;

    const totalFiltradas = computed(() => {
        const total = serverMeta.value && serverMeta.value.total;
        return typeof total === 'number' ? total : sesiones.value.length;
    });

    const totalPaginas = computed(() => {
        const last = serverMeta.value && serverMeta.value.last_page;
        if (typeof last === 'number') return Math.max(1, last);
        return Math.max(1, Math.ceil(totalFiltradas.value / perPageRef.value));
    });

    // La búsqueda vuelve a la primera página y recarga (con debounce). El
    // cambio de per_page también vuelve a la primera y recarga al instante.
    watch(search, () => {
        if (cambiandoEvento) return;
        page.value = 1;
        debouncedFetch();
    });

    watch(perPageRef, () => {
        page.value = 1;
        fetch();
    });

    // Al cambiar de evento se pide la página 1 sin búsqueda. Se silencia el
    // watcher de `search` durante el siguiente tick para que el reset no
    // programe una segunda petición (los watchers de Vue 2 corren en nextTick).
    watch(idActual, (nuevo, anterior) => {
        if (nuevo === anterior || !nuevo) return;
        cambiandoEvento = true;
        search.value = '';
        page.value = 1;
        debouncedFetch.cancel();
        nextTick(() => { cambiandoEvento = false; });
        fetch();
    });

    /**
     * Modelo compatible con TableFooter (value = { page, per_page }). El footer
     * emite un objeto nuevo; aquí se aplica. Si cambió el tamaño de página lo
     * maneja el watcher de perPageRef (que ya recarga); si solo cambió la
     * página, se recarga aquí.
     */
    const model = computed({
        get: () => ({ page: page.value, per_page: perPageRef.value }),
        set: val => {
            if (!val) return;
            const nextPage = val.page !== undefined ? val.page : page.value;
            const nextPerPage = val.per_page !== undefined ? parseInt(val.per_page, 10) : perPageRef.value;

            if (nextPerPage !== perPageRef.value) {
                perPageRef.value = nextPerPage;
                return;
            }
            if (nextPage !== page.value) {
                page.value = nextPage;
                fetch();
            }
        }
    });

    const meta = computed(() => ({ total: totalFiltradas.value }));

    const mostrarBusqueda = computed(() => totalFiltradas.value > perPageRef.value);
    const mostrarPaginacion = computed(() => totalPaginas.value > 1);

    function reset() {
        search.value = '';
        page.value = 1;
    }

    onUnmounted(() => {
        debouncedFetch.cancel();
    });

    return {
        search,
        page,
        perPage: perPageRef,
        sesiones,
        loading,
        error,
        cargado,
        model,
        meta,
        totalPaginas,
        totalFiltradas,
        mostrarBusqueda,
        mostrarPaginacion,
        fetch,
        reset
    };
}
